import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Stage, Layer, Group, Rect } from 'react-konva';
import { dataURLtoFile } from '../builder/ImageUploader.js';
import { DefaultInterationIcon } from './LikeDislikeCanvas.js';

const standard_board_size = { w:800, h:1120 };	// px

const LikeDislikeFinalCanvas = forwardRef((props, ref) => {
	const stage = props.stage;

	const [ full_loaded, setFullLoaded ] = useState(false);

	const bg_image = props.indexed_images?.final_bg_img ?? false;

	const user_answer = props?.user_answer ?? [];

	const like_color = stage?.likedislike_like_color ?? 'rgba(120, 195, 0, 1)';
	const dislike_color = stage?.likedislike_dislike_color ?? 'rgba(215, 35, 60, 1)';

	const stage_ref = useRef();
	const refs = useRef([]);
	const size = useRef({ w:standard_board_size.w, h:standard_board_size.h });
	const canvas_scale = useRef(size.current.w / standard_board_size.w);

	const default_cache_props = { pixelRatio:2, imageSmoothingEnabled:true, offset:5, drawBorder:false };

	const gameInit = () => {
		if(props.adaptToContainer != undefined) {
			props.adaptToContainer((size_received) => {
				size.current.w = size_received;
				size.current.h = (standard_board_size.h / standard_board_size.w) * size.current.w;
				canvas_scale.current = size.current.w / standard_board_size.w;

				setFullLoaded(true);
				
				setTimeout(() => {
					applyAllCaches();
				}, 400);
			}, '.final_image');
		} else {
			setFullLoaded(true);
		}
	};

	useEffect(() => {
		setFullLoaded(false);
		if(props.loaded_status != 'LOADED') return;
		
		gameInit();
	}, [
		props.loaded_status,
		props.indexed_images,
	]);

	const applyAllCaches = () => {
		if(refs.current['layer'] != undefined) return;

		if(refs.current['bg'] != undefined) refs.current['bg'].cache(default_cache_props);

		if(refs.current['all_likes'] != undefined) refs.current['all_likes'].cache(default_cache_props);
		if(refs.current['all_dislikes'] != undefined) refs.current['all_dislikes'].cache(default_cache_props);
		refs.current['layer'].draw();
	};

	useImperativeHandle(ref, () => ({
		to_image(format = 'file') {
			if(stage_ref.current != undefined) {
				let dataURL = stage_ref.current.toDataURL({
					mimeType:'image/jpeg',
					quality:0.8,
					pixelRatio:3,
				});

				if(format == 'base64') return dataURL;
				const file = dataURLtoFile(dataURL, 'filename.jpg');
				return file;
			}
		}
	}), []);

	const global_inline_padding = 60 * canvas_scale.current;
	const global_top_padding = 120 * canvas_scale.current;
	
	const item_size = { w:120 * canvas_scale.current, h:150 * canvas_scale.current };
	const item_separator = 20 * canvas_scale.current;
	const item_border_width = 3 * canvas_scale.current;

	const max_items_per_row = 5;

	if(!full_loaded) return null;

	const liked_items = user_answer.filter((o) => (o.type == 'like'));
	const disliked_items = user_answer.filter((o) => (o.type == 'dislike'));

	const liked_num_rows = Math.ceil(liked_items.length / max_items_per_row);
	const disliked_num_rows = Math.ceil(disliked_items.length / max_items_per_row);
	const total_rows = liked_num_rows + disliked_num_rows;
	const max_cols = Math.min(Math.max(liked_items.length, disliked_items.length), max_items_per_row);

	const extra_y = (total_rows < 4) ? (item_size.h + item_separator) * (4 - total_rows) / 2 : 0;
	const extra_x = ((size.current.w - 2 * global_inline_padding) - max_cols * (item_size.w + item_separator)) / 2;

	const type_separator = 60 * canvas_scale.current - item_separator;
	const type_header_icon_size = 60 * canvas_scale.current;
	const type_header_h = type_header_icon_size + 15 * canvas_scale.current;

	const like_icon_image = props?.indexed_images['like_icon'] ?? false;
	const like_icon_image_scale = (like_icon_image !== false) ? (type_header_icon_size / like_icon_image.image_w) : 1;

	const dislike_icon_image = props?.indexed_images['dislike_icon'] ?? false;
	const dislike_icon_image_scale = (dislike_icon_image !== false) ? (type_header_icon_size / dislike_icon_image.image_w) : 1;

	const bg_image_scale = (bg_image !== false) ? (size.current.w / Math.min((bg_image?.image_w ?? standard_board_size.w), (bg_image?.image_h ?? standard_board_size.h))) : 1;

	return (
		<Stage
			ref={stage_ref}
			className='canvas_cont'
			width={size.current.w}
			height={size.current.h}
		>
			<Layer
				ref={(node) => { refs.current['layer'] = node; }}
			>
				<Rect
					fill='rgba(255, 255, 255, 1)'
					width={size.current.w}
					height={size.current.h}
				/>

				<Rect
					ref={(node) => { refs.current['bg'] = node; }}
					width={size.current.w}
					height={size.current.h}
					fill='black'
					fillPatternImage={bg_image?.fillPatternImage}
					fillPatternRepeat="repeat"
					fillPatternScaleX={bg_image_scale}
					fillPatternScaleY={bg_image_scale}
					fillPriority={(bg_image !== false) ? 'pattern' : 'fill'}
					opacity={(bg_image !== false) ? 1 : 0.05}
					preventDefault={false}
				/>

				<Group
					x={global_inline_padding + extra_x}
					y={global_top_padding + extra_y}
				>
					{(liked_items.length > 0) && (
						<Group
							ref={(node) => { refs.current['all_likes'] = node; }}
							preventDefault={false}
						>
							{(like_icon_image !== false) ? (
								<Rect
									width={type_header_icon_size}
									height={type_header_icon_size}
									fillPatternImage={like_icon_image.fillPatternImage}
									fillPatternRepeat="no-repeat"
									fillPatternScaleX={like_icon_image_scale}
									fillPatternScaleY={like_icon_image_scale}
								/>
							) : (
								<DefaultInterationIcon
									type='like'
									size={type_header_icon_size}
									color={like_color}
								/>
							)}

							{liked_items.map((item, i) => {
								const row = Math.floor(i / max_items_per_row);
								const col = i % max_items_per_row;

								const bg_image = props?.indexed_images[`player_${item.id}`] ?? false;
								const bg_image_scale = (bg_image !== false) ? Math.min((item_size.w / bg_image.image_w), (item_size.h / bg_image.image_h)) : 1;

								return (
									<Group
										key={`like_${item.id}`}
										x={col * (item_size.w + item_separator)}
										y={type_header_h + row * (item_size.h + item_separator)}
									>
										<Rect
											width={item_size.w + 2 * item_border_width}
											height={item_size.h + 2 * item_border_width}
											x={-item_border_width}
											y={-item_border_width}
											fill={like_color}
										/>

										<Rect
											width={item_size.w}
											height={item_size.h}
											fillPatternImage={bg_image.fillPatternImage}
											fillPatternScaleX={bg_image_scale}
											fillPatternScaleY={bg_image_scale}
										/>
									</Group>
								)
							})}
						</Group>
					)}

					{(disliked_items.length > 0) && (
						<Group
							ref={(node) => { refs.current['all_dislikes'] = node; }}
							y={((liked_items.length > 0) ? type_header_h : 0) + liked_num_rows * (item_size.h + item_separator) + type_separator}
							preventDefault={false}
						>
							{(dislike_icon_image !== false) ? (
								<Rect
									width={type_header_icon_size}
									height={type_header_icon_size}
									fillPatternImage={dislike_icon_image.fillPatternImage}
									fillPatternRepeat="no-repeat"
									fillPatternScaleX={dislike_icon_image_scale}
									fillPatternScaleY={dislike_icon_image_scale}
								/>
							) : (
								<DefaultInterationIcon
									type='dislike'
									size={type_header_icon_size}
									color={dislike_color}
								/>
							)}

							{disliked_items.map((item, i) => {
								const row = Math.floor(i / max_items_per_row);
								const col = i % max_items_per_row;

								const bg_image = props?.indexed_images[`player_${item.id}`] ?? false;
								const bg_image_scale = (bg_image !== false) ? Math.min((item_size.w / bg_image.image_w), (item_size.h / bg_image.image_h)) : 1;

								return (
									<Group
										key={`like_${item.id}`}
										x={col * (item_size.w + item_separator)}
										y={type_header_h + row * (item_size.h + item_separator)}
									>
										<Rect
											width={item_size.w + 2 * item_border_width}
											height={item_size.h + 2 * item_border_width}
											x={-item_border_width}
											y={-item_border_width}
											fill={dislike_color}
										/>

										<Rect
											width={item_size.w}
											height={item_size.h}
											fillPatternImage={bg_image.fillPatternImage}
											fillPatternScaleX={bg_image_scale}
											fillPatternScaleY={bg_image_scale}
										/>
									</Group>
								)
							})}
						</Group>
					)}
				</Group>
			</Layer>
		</Stage>
	)
})

export default LikeDislikeFinalCanvas;